<div>
  <ng-container *ngTemplateOutlet="viewerTemplate"></ng-container>
  <!-- Viewer -->
  <ng-template #viewerTemplate>
    <div class="position-relative full-width full-height" #viewerDiv *ngIf="{
        layout: layout$ | async,
        pipelines: micro.pipelines$ | async,
        categories: micro.allCategories$ | async,
        labels: micro.allOptions$ | async,
        steps: micro.steps$ | async,
        collapsedCategories: micro.collapsedCategories$ | async,
        taggingProtocol: micro.taggingProtocol$ | async,
        asset: micro.selectedAsset$ | async,
        analysisContext: micro.analysisContext$ | async,
        nextCase: micro.nextCase$ | async,
        previousCase: micro.previousCase$ | async,
        nextSample: micro.nextSample$ | async,
        previousSample: micro.previousSample$ | async,
        multiSelection: micro.multiSelection$ | async,
        canEdit: micro.canEdit$ | async,
        isReviewingOtherAnalyst: micro.isReviewingOtherAnalyst$ | async,
        editingEnabled: micro.editingEnabled$ | async,
        loadingProtocol: micro.loadingProtocol$ | async,
        loadingAnalysis: micro.findingsLoading$ | async,
        totalCount: micro.totalCount$ | async,
        labelCount: micro.activeAssetVideoRoiCountsByLabel$ | async,
        sampleLabelCount: null,
        totalStats: micro.totalStats$ | async,
        statsMode: micro.statsMode$ | async,
        counterMode: micro.counterMode$ | async,
        selectedLabels: micro.selectedLabels$ | async,
        selectedROIs: micro.selectedVideoRois$ | async,
        activeFindings: micro.activeFindings$ | async,
        items: micro.items$ | async,
        numAssets: micro.totalAssets$ | async,
        syncing: micro.syncing$ | async,
        aiAlgorithms: micro.aiAlgorithms$ | async,
        userAnalysts: micro.userAnalysts$ | async,
        analystSelected: micro.selectedAnalyst$ | async,
        analystStates: micro.analysisStates$ | async,
        currentCase: micro.currentCase$ | async,
        currentSample: micro.currentSample$ | async,
        currentWorkspace: micro.currentWorkspace$ | async,
        tabContext: micro.tabActiveContext$ | async,
        availableViewerModes: micro.availableViewerModes$ | async,
        visibleLabels: micro.visibleLabels$ | async,
        pinnedLabels: micro.pinnedLabels$ | async,
        excludedFromCounter: micro.excludedFromCounter$ | async,
        selectedToolIndex: micro.selectedToolIndex$ | async,
        rulerIcon: micro.rulerIcon$ | async,
        rulerSize: micro.rulerSize$ | async,
        rulerActive: micro.rulerActive$ | async,
        hspan: micro.hspan$ | async,
        selectedPreset: micro.selectedPreset$ | async,
        imageFilters: micro.imageFilters$ | async,
        activeTool: micro.viewerMode$ | async,
        showPinned: micro.showPinned$ | async,
        availableDrawingModes: micro.availableDrawingModes$ | async,
        videoOverlays: micro.videoRoiOverlays$ | async,
        hasUnsavedData: micro.unsynced$ | async,
        selectedIndex: micro.selectedIndex$ | async,
        favAssets: micro.currentAnalystFavouriteAssets$ | async,
        bookmarkedAsset: micro.bookmarkedAsset$ | async,
        acquisitionFields: micro.acquisitionFields$ | async,
        acquisitionDataEditable: micro.dataEditable$ | async,
        acquisitionFieldsMode: micro.acquisitionEdition$ | async,
        activeDrawingMode: micro.drawingMode$ | async,
        protocolPanelCollapsed: micro.protocolPanelCollapsed$ | async,
        progress: micro.getProgress$ | async,
        aiAnalysisInProgress: micro.aiAnalysisInProgress$ | async,
        aiAnalysisStatus: micro.aiAnalysisStatus$ | async,
        aiAnalysisViewOnly: micro.aiAnalysisViewOnly$ | async,
        aiPollingExhausted: aiPollingExhausted$ | async
      } as state">
      <!-- WEB: shared analysis layout — top bar (top), protocol (left), viewer (remaining space).
           Same shell as the microscope-image screen so both screens share one layout skeleton. -->
      <ts-analysis-layout *ngIf="state.layout === 'web'" class="full-height full-width">
        <!-- TOP BAR: breadcrumb · viewer tools · sample actions -->
        <ts-analysis-top-bar layoutTop variant="inline">
          <ts-navigation-breadcrumb barStart menuClass="micro-breadcrumb-menu"
            [levels]="micro.breadcrumbLevels$ | async"
            (optionSelected)="onBreadcrumbSelect($event, state.currentCase?.id)"
            (levelOpened)="onBreadcrumbLevelOpened($event, state.currentSample?.id, state.currentCase?.id)"
            (optionExpanded)="onBreadcrumbOptionExpanded($event)">
          </ts-navigation-breadcrumb>

          <!-- CENTER: viewer/drawing tools as plain toolbar buttons. The drawing (pencil) mode has no
               button of its own — its point/region tools are surfaced directly, and picking one enters
               drawing mode with that tool. The active tool is highlighted via the `applied` class. -->
          <div barCenter class="flex-row"
            *ngIf="authService.hasRoles(['admin', 'analyst', 'analystmanager']) && state.canEdit && state.availableViewerModes">
            <!-- Navigate / move -->
            <button *ngIf="state.availableViewerModes.includes(ViewerMode.idle)" mat-menu-item class="toolbar-action"
              [ngClass]="{ applied: state.activeTool === ViewerMode.idle }"
              [matTooltip]="'tooltip.activate_idle_mode' | translate" [matTooltipShowDelay]="1000" data-cy="tool-idle"
              (click)="onToogleActiveMode(ViewerMode.idle)">
              <i class="ri-drag-move-2-line"></i>
            </button>

            <!-- ROI drawing tools: point / region (each enters drawing mode with that tool) -->
            <ng-container *ngIf="state.availableViewerModes.includes(ViewerMode.drawing)">
              <button mat-menu-item class="toolbar-action" data-cy="button-drawing-center"
                [ngClass]="{ applied: state.activeTool === ViewerMode.drawing && state.activeDrawingMode === DrawingMode.center }"
                [matTooltip]="'core.point' | translate" [matTooltipShowDelay]="1000"
                (click)="onToogleActiveMode(ViewerMode.drawing); micro.setDrawingMode(DrawingMode.center)">
                <i class="ri-focus-3-line"></i>
              </button>
              <button mat-menu-item class="toolbar-action" data-cy="button-drawing-boundingBox"
                [ngClass]="{ applied: state.activeTool === ViewerMode.drawing && state.activeDrawingMode === DrawingMode.boundingBox }"
                [matTooltip]="'core.region' | translate" [matTooltipShowDelay]="1000"
                (click)="onToogleActiveMode(ViewerMode.drawing); micro.setDrawingMode(DrawingMode.boundingBox)">
                <i class="ri-shape-line"></i>
              </button>
            </ng-container>

            <!-- Free-draw tools: brush / lasso (segmentation) -->
            <ng-container *ngIf="state.availableViewerModes.includes(ViewerMode.free)">
              <button mat-menu-item class="toolbar-action" data-cy="button-free-sketch"
                [ngClass]="{ applied: state.activeTool === ViewerMode.free && state.activeDrawingMode === DrawingMode.sketch }"
                (click)="onToogleActiveMode(ViewerMode.free); micro.setDrawingMode(DrawingMode.sketch)">
                <i class="ri-brush-line"></i>
              </button>
              <button mat-menu-item class="toolbar-action" data-cy="button-free-lasso"
                [ngClass]="{ applied: state.activeTool === ViewerMode.free && state.activeDrawingMode === DrawingMode.lasso }"
                (click)="onToogleActiveMode(ViewerMode.free); micro.setDrawingMode(DrawingMode.lasso)">
                <i class="ri-brush-fill"></i>
              </button>
            </ng-container>

            <!-- Select ROIs -->
            <button *ngIf="state.availableViewerModes.includes(ViewerMode.selecting)" mat-menu-item
              class="toolbar-action" [ngClass]="{ applied: state.activeTool === ViewerMode.selecting }"
              [matTooltip]="'tooltip.activate_selecting_mode' | translate" [matTooltipShowDelay]="1000"
              data-cy="tool-selecting" (click)="onToogleActiveMode(ViewerMode.selecting)">
              <i class="ri-cursor-line"></i>
            </button>

            <!-- Delete selected ROIs — always red (destructive) with a count badge. -->
            <button *ngIf="state.activeTool === ViewerMode.selecting && state.selectedROIs?.size" mat-menu-item
              class="toolbar-action delete-action" (click)="micro.removeAllLabels(state.selectedROIs)"
              [matTooltip]="'tooltip.remove_roi' | translate" [matTooltipShowDelay]="1000"
              data-cy="button-remove-selected-rois">
              <i class="ri-delete-bin-6-line"></i>
              <span class="selected-count-badge">{{ state.selectedROIs.size }}</span>
            </button>
          </div>

          <!-- RIGHT: microscope actions, inlined as plain toolbar buttons to match the image screen.
               Video has no mosaic / center-view / zoom / asset-filters, so those buttons are omitted. -->
          <div barEnd class="flex-row">
            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
              [disabled]="!state.hspan" [matTooltip]="'tooltip.ruler' | translate" [matTooltipShowDelay]="1000"
              [matMenuTriggerFor]="rulerMenu" #rulerTrigger="matMenuTrigger" [ngClass]="{ applied: state.rulerActive }"
              data-cy="ruler-button">
              <i class="ri-ruler-line"></i>
            </button>

            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
              [matTooltip]="'tooltip.panel_image_filters' | translate" [matTooltipShowDelay]="1000"
              [matMenuTriggerFor]="filtersMenu" #filtersTrigger="matMenuTrigger"
              [ngClass]="{ applied: getImageFiltersApplied(state.imageFilters) }" data-cy="image-filters-button">
              <i class="ri-equalizer-line"></i>
            </button>

            <ng-container *ngIf="state.isReviewingOtherAnalyst && !state.aiAnalysisViewOnly">
              <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
                [disabled]="state.syncing" [ngClass]="{ applied: state.editingEnabled }" [matTooltip]="
                  (state.editingEnabled ? 'analysis.disable_editing_tooltip' : 'analysis.enable_editing_tooltip')
                    | translate
                " [matTooltipShowDelay]="1000" (click)="micro.toggleEditing(!state.editingEnabled)"
                data-cy="toolbar-toggle-editing">
                <i class="ri-edit-line" *ngIf="!state.editingEnabled"></i>
                <i class="ri-edit-fill" *ngIf="state.editingEnabled"></i>
              </button>
            </ng-container>

            <button *ngIfRole="['admin', 'analyst', 'analystmanager']" mat-menu-item class="toolbar-action"
              [matTooltip]="'analysis.revert_all_changes' | translate" [matTooltipShowDelay]="1000"
              [disabled]="!state.hasUnsavedData || state.syncing" (click)="onDiscardChanges()"
              data-cy="toolbar-revert-all-changes">
              <i class="ri-arrow-go-back-line"></i>
            </button>

            <button [disabled]="state.syncing || !state.hasUnsavedData" mat-menu-item class="toolbar-action"
              matTooltip="{{ 'button.save' | translate }}" [matTooltipShowDelay]="1000"
              [class.bg-warning]="state.hasUnsavedData && !state.syncing" [class.text-white]="state.hasUnsavedData"
              (click)="onSave()" *ngIfRole="['admin', 'analyst', 'analystmanager']" data-cy="toolbar-save-analysis">
              <mat-spinner *ngIf="state.syncing; else videoSaveIcon" class="relative-spinner" value="indeterminate"
                [diameter]="20"></mat-spinner>
              <ng-template #videoSaveIcon>
                <i class="ri-save-line"></i>
              </ng-template>
            </button>

            <!-- More tools (downloads), grouped into one dropdown. -->
            <button mat-menu-item class="toolbar-action" [matMenuTriggerFor]="videoFabMenu" data-cy="FAB-tools">
              <i class="ri-more-2-line"></i>
            </button>

            <button mat-menu-item class="toolbar-action" matTooltip="{{ 'button.close' | translate }}"
              [matTooltipShowDelay]="1000" (click)="exit()" data-cy="toolbar-exit">
              <i class="ri-close-fill"></i>
            </button>
          </div>
        </ts-analysis-top-bar>

        <!-- Ruler / image-filter dropdowns for the bar's ruler & filter buttons. mat-menu is
             template-only (portals on open), so declaring these inside the shell adds no layout box. -->
        <mat-menu #rulerMenu="matMenu" class="widget analysis-tool-menu">
          <ts-ruler-selector [rulerIcon]="state.rulerIcon" [rulerSize]="state.rulerSize"
            (changeRulerIcon)="micro.changeRulerIcon($event)" (changeRulerSize)="micro.changeRulerSize($event)"
            (resetRuler)="onRulerReset()"></ts-ruler-selector>
        </mat-menu>
        <mat-menu #filtersMenu="matMenu" class="widget analysis-tool-menu">
          <ts-image-filters [filters]="state.imageFilters" [selectedPreset]="state.selectedPreset"
            (updateFilter)="micro.updateImageFilter($event)" (presetFilters)="micro.updateImageFilters($event)"
            (resetFilters)="onFiltersReset()"></ts-image-filters>
        </mat-menu>
        <!-- More tools dropdown: bulk download. -->
        <mat-menu #videoFabMenu="matMenu" class="widget analysis-tool-menu">
          <button *ngIf="state.numAssets > 1" mat-menu-item [disabled]="!featureService.canUse('downloadAvailable')"
            (click)="downloadAssets(true, state.items, state.currentSample?.name)"
            data-cy="button-download-assets-zip">
            <i class="ri-folder-download-line"></i>
            <span>{{ 'button.download_zip_images' | translate }}</span>
          </button>
        </mat-menu>

        <!-- PROTOCOL PANEL (left column of the shell body). Analysis roles always get it
             (protocol/labels); technician roles only when there is acquisition data to edit. -->
        <div class="protocol-panel-container" [class.protocol-collapsed]="state.protocolPanelCollapsed"
          *ngIf="authService.hasRoles(['admin', 'analyst', 'analystmanager']) || state.acquisitionFields?.length">
          <!-- Collapse/expand affordance: a small arrow centred on the panel's inner edge. -->
          <button *ngIfRole="['admin', 'analyst', 'analystmanager']" class="protocol-collapse-toggle"
            [matTooltip]="(state.protocolPanelCollapsed ? 'tooltip.show_panel' : 'tooltip.hide_panel') | translate"
            [matTooltipShowDelay]="0" (click)="micro.toggleProtocolPanel()" data-cy="toggle-protocol-panel-button">
            <i [class]="state.protocolPanelCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'"></i>
          </button>
          <ts-sample-analysis-panel #analysisPanel [collapsed]="state.protocolPanelCollapsed"
            [dataEditable]="state.acquisitionDataEditable" [taggingProtocol]="state.taggingProtocol"
            [multiSelection]="state.multiSelection" [selectedAsset]="state.asset" [canEdit]="state.canEdit"
            [sampleId]="state.currentSample?.id" [currentAnalyst]="state.analystSelected"
            [loadingProtocol]="state.loadingProtocol" [loadingAnalysis]="state.loadingAnalysis"
            [analysisContext]="state.analysisContext" [pipelines]="state.pipelines" [categories]="state.categories"
            [steps]="state.steps" [collapsedCategories]="state.collapsedCategories" [options]="state.labels"
            [showPinned]="state.showPinned" [acquisitionFields]="state.acquisitionFields"
            [acquisitionEdition]="state.acquisitionFieldsMode" [selectedLabels]="state.selectedLabels"
            [visibleLabels]="state.visibleLabels" [pinnedLabels]="state.pinnedLabels" [labelCount]="state.labelCount"
            [sampleLabelCount]="state.sampleLabelCount" [statsMode]="state.statsMode" [counterMode]="state.counterMode"
            [totalCount]="state.totalCount" [excludedFromCounter]="state.excludedFromCounter"
            [activeFindings]="state.activeFindings" (toogleMode)="micro.setProtocolContext($event)"
            (setStatsMode)="micro.setStatsMode($event)" (toggleMulti)="micro.toggleMultiSelection()"
            (tooglePinned)="micro.togglePinnedOptions()" (toogleTaskPanel)="micro.collapseCategory($event)"
            (acquisitionEditionMode)="micro.toogleAcquisitionEdition($event)"
            (acquisitionFieldChange)="micro.updateAssetDataField($event)" (selectLabel)="micro.onSelectLabel($event)"
            (newLabel)="onNewLabel()" (pinLabel)="micro.onPinnedLabel($event)"
            (counterLabel)="micro.onCounterLabel($event)" (visibleLabel)="micro.onVisibleLabel($event)"
            (createFinding)="micro.createFinding($event)"
            (updateFinding)="micro.updateFinding($event)"></ts-sample-analysis-panel>
        </div>

        <!-- VIEWER AREA (main column) — the video viewer fills it; its timeline / bookmarks track and
             its progress bar ride within it. -->
        <div class="micro-viewer-area">
          <ng-container *ngTemplateOutlet="viewerContentTpl"></ng-container>
        </div>
      </ts-analysis-layout>

      <!-- MOBILE: full-bleed viewer with the mobile analysis panel + a single actions row. -->
      <div *ngIf="state.layout === 'mobile'" class="position-relative full-width full-height">
        <ng-container *ngTemplateOutlet="viewerContentTpl"></ng-container>

        <ng-container [ngTemplateOutlet]="mobileAnalysisPanelTemplate"></ng-container>
        <div class="analysis-panel-and-actions-container mobile">
          <div class="sample-actions-and-progress-container">
            <ng-container *ngTemplateOutlet="sampleToolsTpl"></ng-container>
          </div>
        </div>
      </div>

      <!-- Shared viewer content: the video viewer + its timeline/bookmarks track. Outletted once into
           whichever layout branch (web shell viewer-area or mobile full-bleed) is active, so the
           viewer with its bindings/directives lives in a single place. `state` resolves from the
           enclosing *ngIf…as state. -->
      <ng-template #viewerContentTpl>
        <ts-video-viewer tsVideoRoisViewer tsVideoRoisDrawer [setViewerMode]="micro.setViewerMode.bind(micro)"
          videoTrackPanel #videoROIs="videoROIs" #videoViewer [videoOverlays]="state.videoOverlays"
          [activeMode]="state.activeTool" [sources]="state.items" [selectedIndex]="state.selectedIndex"
          [videoBlobUrl]="micro.videoBlobUrl$ | async" [isIOS]="isIOS" [isMobile]="isMobile"
          (resizeEvent)="videoBarResized()" (videoROIAdded)="onVideoROIAdded($event)"
          (roiTimeUpdated)="onROITimeUpdated($event)" (assetChanged)="onAssetChanged($event)"
          (unselectRois)="micro.clearSelectedVideoRois()" (delete)="micro.removeAllLabels(state.selectedROIs)">
          <!-- Progress bar content, i.e. bookmarks -->
          <div #scrollableWrapper class="scrollable-timeline-wrapper">
            <div class="scrollable-timeline-container">
              <div class="scrollable-timeline-content">
                <div *ngFor="let videoROI of micro.videoRoiOverlays$ | async" class="roi-track" id="roiTrack">
                  <div class="roi-bar" [attr.data-id]="videoROI.id"
                    [style.left]="videoViewer.relativeTime(videoROI.t1) + '%'"
                    [style.width]="videoViewer.relativeTime(videoROI.t2 - videoROI.t1) + '%'"
                    [style.background-color]="videoROI.color" [class.deletable]="state.activeTool === 'removing'"
                    (click)="videoViewer.gotoBookmark($event, videoROI); micro.selectVideoRois([videoROI.id])"
                    [style.box-shadow]="videoROI.selected ? 'rgb(0, 153, 187) 0px 0px 5px 2px' : 'none'" cdkDrag
                    cdkDragLockAxis="x" (mousedown)="videoROIs.onMouseDown($event)"
                    (mouseup)="videoROIs.onMouseUp($event)"
                    (cdkDragStarted)="videoROIs.startDraggingROI(videoROI, $event)"
                    (cdkDragMoved)="videoROIs.onDragMove(videoROI, $event)"
                    (cdkDragEnded)="videoROIs.processBookMarkDrag(videoROI, $event)" [matTooltip]="videoROI.tooltip">
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ts-video-viewer>
      </ng-template>

      <!-- Mobile Analysis Panel Template -->
      <ng-template #mobileAnalysisPanelTemplate>
        <ng-container *ngIf="micro.selectedVideoRois$ | async as selectedROIs">
          <ts-mobile-analysis-panel [hidden]="(showMobileAnalysisPanel$ | async) === false"
            (onclose)="closeMobileAnalysisPanel()"
            (delete)="micro.removeAllLabels(selectedROIs)"></ts-mobile-analysis-panel>
        </ng-container>
      </ng-template>

      <!-- Sample analysis tools: used by the mobile actions row. `state` resolves from the enclosing
           *ngIf…as state. -->
      <ng-template #sampleToolsTpl>
        <ts-sample-analysis-tools [numAssets]="state.numAssets" [mosaicMode]="false" [layout]="state.layout"
          [selectedAsset]="state.asset" [selectedIndex]="state.selectedIndex"
          [availableAIanalysis]="featureService.canUse('ai')"
          [downloadAvailable]="featureService.canUse('downloadAvailable')" [hasUnsavedData]="state.hasUnsavedData"
          [syncing]="state.syncing" [mosaicButtonDisabled]="true" [sectorProcessing]="state.aiAnalysisViewOnly"
          [aiPollingExhausted]="state.aiPollingExhausted" [aiAnalysisStatus]="state.aiAnalysisStatus"
          [aiAnalysisViewOnly]="state.aiAnalysisViewOnly" [aiAlgorithms]="state.aiAlgorithms"
          [analystStates]="state.analystStates" [userAnalysts]="state.userAnalysts"
          [analystSelected]="state.analystSelected" [isAnalystCurrentUser]="state.canEdit" [canEdit]="state.canEdit"
          [editingEnabled]="state.editingEnabled" [isReviewingOtherAnalyst]="state.isReviewingOtherAnalyst"
          [imageFilters]="state.imageFilters" [imageFiltersApplied]="imageFiltersApplied(state.imageFilters)"
          [selectedPreset]="state.selectedPreset" [rulerActive]="state.rulerActive" [rulerIcon]="state.rulerIcon"
          [rulerSize]="state.rulerSize" [panelContext]="state.tabContext" (exit)="exit()" (save)="onSave()"
          (downloadAll)="downloadAssets(true, state.items, state.currentSample?.name)"
          (updateFilter)="micro.updateImageFilter($event)" (updateFilters)="onUpdateImageFiltersBulk($event)"
          (resetFilters)="micro.resetImageFilters()" (resetRuler)="micro.resetRuler()"
          (analystChange)="onAnalystChange($event)" (toogleReference)="micro.setReference()"
          (tooglePanel)="micro.setTabContext($event)" (changeRulerIcon)="micro.changeRulerIcon($event)"
          (changeRulerSize)="micro.changeRulerSize($event)" (downloadAsset)="onDownloadAsset()"
          (revertAllChanges)="onDiscardChanges()">
        </ts-sample-analysis-tools>
      </ng-template>
    </div>
  </ng-template>
</div>
